// PoliciesAdminPage — where a director publishes a policy version.
//
// THIS SCREEN IS THE REASON THE FEATURE EXISTS AT ALL. The endpoint shipped
// before the button did, which made publishing a curl-only act and — because
// the staff nav item is gated on a published version existing — left the whole
// feature unreachable through the UI. An endpoint with no button is a feature
// nobody can use.
//
// Three inputs carry rulings and the copy says so rather than assuming the
// director remembers:
//
//   * THE VERSION LABEL IS TYPED, NEVER READ OFF THE PAGE. The source
//     documents carry three different spellings of the same date.
//   * THE DEADLINE LIVES ON THE PUBLISH — 14 days, shortenable here.
//   * requires_reacknowledgement DEFAULTS TRUE. Unticking it is a
//     correction-versus-substantive judgement, which is why this screen is
//     director-only.

import { useState, useEffect, useCallback } from 'react';
import { DocumentArrowUpIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';
import toast from 'react-hot-toast';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US',
  { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

function PublishForm({ doc, onPublished }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('14');
  const [reack, setReack] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Choose the policy PDF.');
    if (!label.trim()) return toast.error('Type the version label — it is never read off the document.');
    setBusy(true);
    const fd = new FormData();
    fd.append('document', file);
    fd.append('version_label', label.trim());
    if (reack) fd.append('deadline_days', days);
    fd.append('requires_reacknowledgement', reack ? 'true' : 'false');
    if (notes.trim()) fd.append('notes', notes.trim());
    try {
      const { data } = await api.post(`/policies/${doc.key}/versions`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(
        `Published ${data.document} ${data.version_label}`
        + (data.obligations_created ? ` — ${data.obligations_created} obligation${data.obligations_created === 1 ? '' : 's'} created.` : '.')
      );
      setFile(null); setLabel(''); setNotes('');
      onPublished();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not publish.');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="px-5 py-4 bg-gray-50/60 border-t border-gray-100 space-y-3">
      <div>
        <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Document</label>
        <input type="file" accept=".pdf,.doc,.docx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-700" />
        <p className="text-[11px] text-gray-500 mt-1">
          Publishing always writes a new file. Nothing is ever written over an earlier version.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
            Version label — REQUIRED
          </label>
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. v1.0"
            className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
          <p className="text-[11px] text-gray-500 mt-1">
            Typed, never read off the document. Every acknowledgment will name it.
          </p>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
            Acknowledge within
          </label>
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="90" value={days} disabled={!reack}
              onChange={(e) => setDays(e.target.value)}
              className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:bg-gray-100" />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            A paused person’s clock does not run.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={reack} onChange={(e) => setReack(e.target.checked)}
          className="mt-0.5" />
        <span className="text-sm text-gray-800">
          Staff must acknowledge this version
          <span className="block text-[11px] text-gray-500">
            Leave ticked for any substantive change. Untick ONLY for a correction — a typo
            fix that forces everyone to click again trains people to click without reading.
            Either way their file will show that a newer version exists.
          </span>
        </span>
      </label>

      <div>
        <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
          What changed (optional)
        </label>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
      </div>

      <button type="submit" disabled={busy}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 px-4 py-2 rounded-xl">
        <DocumentArrowUpIcon className="w-4 h-4" />
        {busy ? 'Publishing…' : `Publish ${doc.title}`}
      </button>
    </form>
  );
}

function DocumentCard({ doc, onPublished }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);

  const loadVersions = useCallback(async () => {
    try {
      const { data } = await api.get(`/policies/${doc.key}/versions`);
      setVersions(data);
    } catch { setVersions([]); }
  }, [doc.key]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{doc.title}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {doc.acknowledgment_key
              ? <>Acknowledged as part of <span className="font-medium">{doc.acknowledgment_key.replace(/_/g, ' ')}</span></>
              : <>Reference only — no acknowledgment</>}
          </p>
          <p className="text-xs mt-1">
            {doc.published
              ? <span className="text-gray-700">Current: <span className="font-semibold">{doc.version_label}</span>, published {fmt(doc.published_at)}</span>
              : <span className="text-amber-700 font-medium">Not published yet</span>}
          </p>
        </div>
        <button onClick={() => setOpen((o) => !o)}
          className="text-sm font-semibold text-orange-600 hover:text-orange-700 flex-shrink-0">
          {open ? 'Cancel' : doc.published ? 'Publish new version' : 'Publish v1'}
        </button>
      </div>

      {versions && versions.length > 0 && (
        <ul className="px-5 pb-3 space-y-1">
          {versions.map((v) => (
            <li key={v.id} className="text-[11px] text-gray-500 flex items-center gap-2">
              <ClockIcon className="w-3 h-3 flex-shrink-0" />
              <span className={v.is_current ? 'font-semibold text-gray-700' : ''}>{v.version_label}</span>
              <span>· {fmt(v.published_at)}</span>
              {v.published_by_name && <span>· {v.published_by_name}</span>}
              {!v.requires_reacknowledgement && <span>· correction, no re-acknowledgment</span>}
              {v.outstanding > 0 && <span className="text-amber-700">· {v.outstanding} outstanding</span>}
            </li>
          ))}
        </ul>
      )}

      {open && <PublishForm doc={doc} onPublished={() => { setOpen(false); loadVersions(); onPublished(); }} />}
    </div>
  );
}

export default function PoliciesAdminPage() {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/policies');
      setDocs(data.documents);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load policies.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (!docs) return null;

  return (
    <div className="animate-slide-up max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Publish policies</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Publishing sets an acknowledgment deadline for every employee and can gate a
          new hire’s first shift, so it is director-only. A published version can never
          be edited — a revision is always a new version, and the old one stays on the
          file of everyone who acknowledged it.
        </p>
      </div>
      {docs.map((d) => <DocumentCard key={d.key} doc={d} onPublished={load} />)}
    </div>
  );
}
